import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Paper,
  useTheme,
  useMediaQuery,
  Divider,
  Tooltip,
  Dialog,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  EmojiEvents as TrophyIcon,
  SportsEsports as GameIcon,
  CalendarToday as CalendarIcon,
  Description as RulesIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import PageTitle from '../components/PageTitle';
import TournamentCard from '../components/TournamentCard';
import tournamentService from '../services/tournamentService';
import BottomNav from '../components/BottomNav';
import SectionTitle from '../components/SectionTitle';

const TournamentsPage = () => {
  const [tournaments, setTournaments] = useState([]);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [newTournament, setNewTournament] = useState({
    name: '',
    type: 'single_elimination',
    startDate: '',
    rules: ''
  });
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const tournamentTypes = [
    { value: 'single_elimination', label: 'Eliminación Simple', icon: <GameIcon /> },
    { value: 'double_elimination', label: 'Doble Eliminación', icon: <GameIcon /> },
    { value: 'round_robin', label: 'Todos contra Todos', icon: <GameIcon /> }
  ];

  useEffect(() => {
    const fetchTournaments = async () => {
      try {
        const data = await tournamentService.getTournaments();
        setTournaments(data);
      } catch (error) {
        console.error('Error fetching tournaments:', error);
        setError('Error al cargar los torneos. Por favor intenta nuevamente.');
      }
    };

    fetchTournaments();
  }, []);

  const handleCreateTournament = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      // Validate form
      if (!newTournament.name.trim()) {
        throw new Error('El nombre del torneo es requerido');
      }
      if (!newTournament.startDate) {
        throw new Error('La fecha de inicio es requerida');
      }

      const createdTournament = await tournamentService.createTournament(newTournament);
      setTournaments([...tournaments, createdTournament]);
      setOpenCreateDialog(false);
      setNewTournament({
        name: '',
        type: 'single_elimination',
        startDate: '',
        rules: ''
      });
      
      // Redirect to tournament details page
      navigate(`/tournaments/${createdTournament.id}`);
    } catch (error) {
      console.error('Error creating tournament:', error);
      setError(error.message || 'Error al crear el torneo. Por favor intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTournamentClick = (tournamentId) => {
    navigate(`/tournaments/${tournamentId}`);
  };

  const activeTournaments = tournaments.filter(t => t.status !== 'completed');
  const completedTournaments = tournaments.filter(t => t.status === 'completed');

  return (
    <>
      <Box sx={{ 
        p: { xs: 1, sm: 2 },
        mt: '0px',
        mb: '56px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: '800px',
        mx: 'auto',
        position: 'relative'
      }}>
        <Stack spacing={2} sx={{ width: '100%' }}>
          {/* Header */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2
          }}>
            <Typography 
              variant="h5" 
              fontWeight="bold"
              color="secondary"
            >
              Torneos
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenCreateDialog(true)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 2,
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.dark'
                }
              }}
            >
              Crear Torneo
            </Button>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                borderRadius: 2,
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
            >
              {error}
            </Alert>
          )}

          {/* Active Tournaments */}
          <SectionTitle title="Torneos Activos">
            <Stack spacing={2}>
              {activeTournaments.map((tournament) => (
                <TournamentCard 
                  key={tournament.id}
                  tournament={tournament}
                  onClick={() => handleTournamentClick(tournament.id)}
                />
              ))}
              {activeTournaments.length === 0 && (
                <Paper 
                  sx={{ 
                    p: 3, 
                    textAlign: 'center',
                    bgcolor: 'background.paper',
                    borderRadius: 2
                  }}
                >
                  <Typography color="text.secondary">
                    No hay torneos activos en este momento
                  </Typography>
                </Paper>
              )}
            </Stack>
          </SectionTitle>

          {/* Completed Tournaments */}
          <SectionTitle title="Torneos Finalizados">
            <Stack spacing={2}>
              {completedTournaments.map((tournament) => (
                <TournamentCard 
                  key={tournament.id}
                  tournament={tournament}
                  onClick={() => handleTournamentClick(tournament.id)}
                  variant="completed"
                />
              ))}
              {completedTournaments.length === 0 && (
                <Paper 
                  sx={{ 
                    p: 3, 
                    textAlign: 'center',
                    bgcolor: 'background.paper',
                    borderRadius: 2
                  }}
                >
                  <Typography color="text.secondary">
                    No hay torneos finalizados
                  </Typography>
                </Paper>
              )}
            </Stack>
          </SectionTitle>
        </Stack>
      </Box>

      {/* Create Tournament Dialog */}
      <Dialog 
        open={openCreateDialog} 
        onClose={() => setOpenCreateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom color="secondary" fontWeight="bold">
            Crear Nuevo Torneo
          </Typography>

          <Box component="form" onSubmit={handleCreateTournament}>
            <Stack spacing={2}>
              {/* Tournament Name */}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Nombre del Torneo
                </Typography>
                <TextField
                  fullWidth
                  value={newTournament.name}
                  onChange={(e) => setNewTournament({ ...newTournament, name: e.target.value })}
                  placeholder="Ingresa el nombre del torneo"
                  size="small"
                  sx={{
                    mt: 0.5,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5
                    }
                  }}
                />
              </Box>

              {/* Tournament Type */}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Tipo de Torneo
                </Typography>
                <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                  <Select
                    value={newTournament.type}
                    onChange={(e) => setNewTournament({ ...newTournament, type: e.target.value })}
                    sx={{
                      borderRadius: 1.5,
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }
                    }}
                  >
                    {tournamentTypes.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {option.icon}
                          {option.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Start Date */}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Fecha de Inicio
                </Typography>
                <TextField
                  fullWidth
                  type="date"
                  value={newTournament.startDate}
                  onChange={(e) => setNewTournament({ ...newTournament, startDate: e.target.value })}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    mt: 0.5,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5
                    }
                  }}
                />
              </Box>

              {/* Rules */}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Reglas (opcional)
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={newTournament.rules}
                  onChange={(e) => setNewTournament({ ...newTournament, rules: e.target.value })}
                  placeholder="Ingresa las reglas del torneo"
                  size="small"
                  sx={{
                    mt: 0.5,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5
                    }
                  }}
                />
              </Box>

              {/* Action Buttons */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: 1,
                mt: 2
              }}>
                <Button 
                  onClick={() => setOpenCreateDialog(false)}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                    bgcolor: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.dark'
                    }
                  }}
                >
                  {submitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Crear Torneo'
                  )}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Dialog>

      {/* Bottom Navigation */}
      <BottomNav newMatchButtonSize={0.4} />
    </>
  );
};

export default TournamentsPage; 